import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  getSettlementsHistory,
  exportSettlementsHistoryCsv,
} from "../api";

import { listVenues } from "../../../pages/SuperAdmin/Venues/venuesApi";

import FormButton from "../../../components/form/FormButton";
import FormField from "../../../components/form/FormField";
import FormInput from "../../../components/form/FormInput";
import FormSelect from "../../../components/form/FormSelect";

import "../components/SettlementsDashboard.css";

const fmt = new Intl.NumberFormat("en-IN");
const money = (value) => `₹${fmt.format(Number(value || 0))}`;

const formatDate = (value) => {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const formatDateTime = (value) => {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const getAdminBasePath = (pathname = "") => {
  if (pathname.startsWith("/country-admin")) return "/country-admin";
  return "/super-admin";
};

const defaultFilters = {
  q: "",
  from: "",
  to: "",
  venueId: "",
  status: "SETTLED",
};

const SettlementHistoryPage = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const basePath = useMemo(() => getAdminBasePath(pathname), [pathname]);

  const [filters, setFilters] = useState(defaultFilters);
  const [venueOptions, setVenueOptions] = useState([]);

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const cleanParams = (sourceFilters, nextPage) => {
    const params = {
      status: sourceFilters.status || "SETTLED",
      q: sourceFilters.q || undefined,
      from: sourceFilters.from || undefined,
      to: sourceFilters.to || undefined,
      venueId: sourceFilters.venueId || undefined,
      page: nextPage,
      limit,
    };

    Object.keys(params).forEach((key) => {
      if (params[key] === "" || params[key] === undefined || params[key] === null) {
        delete params[key];
      }
    });

    return params;
  };

  const loadVenues = useCallback(async () => {
    try {
      const res = await listVenues({ page: 1, pageSize: 500, q: "" });

      const options = (res?.items || []).map((venue) => ({
        id: venue._id || venue.id,
        name: venue.location?.city
          ? `${venue.name} – ${venue.location.city}`
          : venue.name || venue._id,
      }));

      setVenueOptions(options);
    } catch (error) {
      console.error("loadVenues error:", error);
      setVenueOptions([]);
    }
  }, []);

  const loadHistory = useCallback(
    async ({ nextPage = page, nextFilters = filters } = {}) => {
      setLoading(true);

      try {
        const data = await getSettlementsHistory(
          cleanParams(nextFilters, nextPage)
        );

        setItems(data.items || []);
        setSummary(data.summary || {});
        setPage(data.page || nextPage || 1);
        setPages(data.pages || 1);
        setTotal(data.total || 0);
      } catch (error) {
        console.error("getSettlementsHistory error:", error);
        toast.error("Failed to load settlement history");

        setItems([]);
        setSummary({});
        setPage(1);
        setPages(1);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [filters, limit, page]
  );

  useEffect(() => {
    loadVenues();
    loadHistory({ nextPage: 1, nextFilters: defaultFilters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilter = (key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleApply = () => {
    setPage(1);
    loadHistory({ nextPage: 1, nextFilters: filters });
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    setPage(1);
    loadHistory({ nextPage: 1, nextFilters: defaultFilters });
  };

  const handleExport = async () => {
    setExporting(true);

    try {
      const params = cleanParams(filters, page);
      delete params.page;
      delete params.limit;

      const result = await exportSettlementsHistoryCsv(params);

      toast.success(`CSV exported: ${result.filename}`);
    } catch (error) {
      console.error("exportSettlementsHistoryCsv error:", error);
      toast.error("CSV export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleViewDetails = (settlementId) => {
    if (!settlementId) return;
    navigate(`${basePath}/settlements/review/${settlementId}`);
  };

  const summaryCards = {
    totalSettlements: summary.totalSettlements ?? total ?? 0,
    settledAmount: summary.settledAmount || 0,
    avgVenuePayout: summary.avgVenuePayout || 0,
    lastSettlementDate: summary.lastSettlementDate || null,
    lastSettlementLabel: summary.lastSettlementLabel || "",
  };

  return (
    <div className="setd setd-history-page">
      <div className="setd-breadcrumb">
        <button
          type="button"
          onClick={() => navigate(`${basePath}/settlements`)}
        >
          Settlements
        </button>
        <span>›</span>
        <span>Settlement History</span>
      </div>

      <div className="setd-review-page-head">
        <div>
          <h1 className="setd-title">Settlement History</h1>
          <p className="setd-subtitle">
            Track completed and past settlement records across venues.
          </p>
        </div>

        <div className="setd-review-page-actions">
          <button
            type="button"
            className="setd-btn setd-btn-secondary"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? "Exporting…" : "Export"}
          </button>

          <button
            type="button"
            className="setd-btn setd-btn-primary"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? "Downloading…" : "Download Report"}
          </button>
        </div>
      </div>

      <section className="setd-history-kpis">
        <div className="setd-history-kpi">
          <span className="setd-history-kpi-icon">▤</span>
          <div>
            <p>Total Settlements</p>
            <strong>{summaryCards.totalSettlements}</strong>
            <small>Filtered settlement records</small>
          </div>
        </div>

        <div className="setd-history-kpi">
          <span className="setd-history-kpi-icon">₹</span>
          <div>
            <p>Settled Amount</p>
            <strong>{money(summaryCards.settledAmount)}</strong>
            <small>Total venue payout</small>
          </div>
        </div>

        <div className="setd-history-kpi">
          <span className="setd-history-kpi-icon">↗</span>
          <div>
            <p>Avg. Venue Payout</p>
            <strong>{money(summaryCards.avgVenuePayout)}</strong>
            <small>Per settlement</small>
          </div>
        </div>

        <div className="setd-history-kpi">
          <span className="setd-history-kpi-icon">□</span>
          <div>
            <p>Last Settlement Date</p>
            <strong>{formatDate(summaryCards.lastSettlementDate)}</strong>
            <small>{summaryCards.lastSettlementLabel || "—"}</small>
          </div>
        </div>
      </section>

      <section className="setd-history-filter-card">
        <div className="setd-history-filter-grid">
          <FormField label="Search">
            <FormInput
              type="text"
              value={filters.q}
              onChange={(event) => setFilter("q", event.target.value)}
              placeholder="Search UTR, venue, cycle, notes"
            />
          </FormField>

          <FormField label="From">
            <FormInput
              type="date"
              value={filters.from}
              onChange={(event) => setFilter("from", event.target.value)}
            />
          </FormField>

          <FormField label="To">
            <FormInput
              type="date"
              value={filters.to}
              onChange={(event) => setFilter("to", event.target.value)}
            />
          </FormField>

          <FormField label="Venue">
            <FormSelect
              value={filters.venueId}
              onChange={(event) => setFilter("venueId", event.target.value)}
            >
              <option value="">All Venues</option>
              {venueOptions.map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {venue.name || venue.id}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField label="Status">
            <FormSelect
              value={filters.status}
              onChange={(event) => setFilter("status", event.target.value)}
            >
              <option value="SETTLED">Settled</option>
              <option value="READY_FOR_SETTLEMENT">Ready For Settlement</option>
              <option value="all">All Statuses</option>
            </FormSelect>
          </FormField>

          <div className="setd-history-filter-actions">
            <FormButton type="button" variant="primary" onClick={handleApply}>
              {loading ? "Loading…" : "Apply Filters"}
            </FormButton>

            <FormButton type="button" variant="secondary" onClick={handleReset}>
              Reset
            </FormButton>
          </div>
        </div>
      </section>

      <section className="setd-history-records-card">
        <div className="setd-bookings-head">
          <h4>All Settlement Records</h4>
          <span>{total || 0} record(s)</span>
        </div>

        {loading ? (
          <div className="setd-loading">Loading settlement history…</div>
        ) : items.length === 0 ? (
          <div className="setd-empty">No settlement history found.</div>
        ) : (
          <div className="setd-history-wrap setd-history-wrap-page">
            <table className="setd-history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Venue</th>
                  <th>Period</th>
                  <th>UTR</th>
                  <th className="setd-money-col">Venue Receives</th>
                  <th className="setd-money-col">Collected</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {items.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <div>{formatDate(item.settledAt)}</div>
                      <small>{formatDateTime(item.settledAt)}</small>
                    </td>

                    <td>
                      <div>{item.venue?.name || "—"}</div>
                      <small>
                        {item.venue?.location?.city ||
                          item.venue?.country ||
                          "—"}
                      </small>
                    </td>

                    <td>
                      <div>{item.cycleLabel || "Settlement"}</div>
                      <small>
                        {formatDate(item.periodStart)} –{" "}
                        {formatDate(item.periodEnd)}
                      </small>
                    </td>

                    <td>
                      <strong>{item.utrNumber || "—"}</strong>
                    </td>

                    <td className="setd-money-col">
                      {money(item.venueSettlement)}
                    </td>

                    <td className="setd-money-col">
                      {money(item.totalCollected)}
                    </td>

                    <td>
                      <span
                        className={`setd-status-pill ${
                          item.status === "SETTLED" ? "settled" : ""
                        }`}
                      >
                        {item.status || "—"}
                      </span>
                    </td>

                    <td style={{ textAlign: "right" }}>
                      <button
                        type="button"
                        className="setd-link-btn"
                        onClick={() => handleViewDetails(item._id)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="setd-history-footer">
          <div className="setd-history-total">
            Showing page {page || 1} of {pages || 1}
          </div>

          <div className="setd-history-pager">
            <button
              type="button"
              className="setd-btn setd-btn-secondary"
              disabled={page <= 1 || loading}
              onClick={() => loadHistory({ nextPage: page - 1 })}
            >
              Previous
            </button>

            <span>{page || 1}</span>

            <button
              type="button"
              className="setd-btn setd-btn-secondary"
              disabled={page >= pages || loading}
              onClick={() => loadHistory({ nextPage: page + 1 })}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      <div className="setd-info">
        <div className="setd-info-left">
          <span className="setd-info-icon">i</span>
          <div>
            <p className="setd-info-title">Settlement records are generated after payout confirmation.</p>
            <p className="setd-info-text">
              Records can be exported for reconciliation and finance reporting.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettlementHistoryPage;